import { boardOf } from './rotationCriteria.js';
import {
  clauseLabel,
  durationTH,
  isLevelDown,
  leafUnit,
  summarise,
  type DraftItem,
} from './rotationDraft.js';
import { fileName } from './exportTable.js';

/**
 * เอกสารเสนอของรอบโยกย้าย — PDF แบบหนังสือราชการ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมไม่ใช้ exportTable.ts เหมือนหน้าจออื่น
 * ────────────────────────────────────────────────────────────────
 *
 * `exportTable.ts` ออก **ตาราง** ซึ่งถูกต้องสำหรับบัญชีรายชื่อที่เอาไปทำงานต่อ
 * แต่สิ่งที่ออกจากหน้านี้คือ **เอกสารแนบท้ายบันทึกเสนอ** ที่จะถูกปรินต์
 * วางบนโต๊ะผู้มีอำนาจสั่งบรรจุ แล้วเซ็นบนกระดาษแผ่นนั้น
 *
 * ของเดิมออกเป็นตารางแนวนอน 13 คอลัมน์ ซึ่งมีปัญหาสามข้อพร้อมกัน
 *   1. คอลัมน์ขวาสุดถูกตัดหายนอกขอบกระดาษ — ข้อมูลหายไปเงียบ ๆ
 *   2. คำเตือนสิบเอ็ดข้อกองเป็นพรืดบนหัวกระดาษ ไม่มีใครอ่าน
 *   3. ไม่มีที่ให้เซ็น ซึ่งแปลว่าเอกสารนี้ใช้เสนอจริงไม่ได้เลย
 *
 * ⚠ Excel ยังใช้ exportTable.ts เหมือนเดิม และต้องเป็นอย่างนั้นต่อไป
 *   PDF คือเอกสาร Excel คือข้อมูล — สองอย่างนี้ไม่ใช่รูปแบบไฟล์ของสิ่งเดียวกัน
 *
 * ────────────────────────────────────────────────────────────────
 *  ข้อห้ามเรื่องอักขระ
 * ────────────────────────────────────────────────────────────────
 *
 * ⚠ ฟอนต์ที่ฝังใน PDF เป็น Sarabun ที่ตัดชุดอักขระแล้ว (317 ตัว)
 *   ตรวจแล้วว่า **ไม่มี** ⚠ (U+26A0) · ✓ (U+2713) · ✕ (U+2715)
 *   ตัวที่มี — • (U+2022) · – — · · · เลขไทย ๐-๙
 *   ใส่ตัวที่ไม่มีลงไป จะได้สี่เหลี่ยมเปล่าในไฟล์ โดยหน้าจอไม่มีอะไรผิด
 *   (นี่คือสาเหตุของสี่เหลี่ยมที่ขึ้นหน้าทุกบรรทัดคำเตือนในไฟล์รุ่นก่อน)
 */

const TH_DIGITS = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙'];

/** เลขไทย — หัวข้อและลำดับในหนังสือราชการใช้เลขไทย ไม่ใช่เลขอารบิก */
function thNum(n: number): string {
  return String(n)
    .split('')
    .map((d) => TH_DIGITS[Number(d)] ?? d)
    .join('');
}

function thaiDate(d: Date): string {
  return new Intl.DateTimeFormat('th-TH', { dateStyle: 'long' }).format(d);
}

const NAVY = '#16336b';
const INK = '#101a2c';
const INK2 = '#3d4a61';
const MUTED = '#67748c';
const LINE = '#dde3ec';
const RULE = '#94a0b5';

type Content = Record<string, unknown>;

/**
 * หัวข้อ + เนื้อหาของข้อหนึ่ง
 *
 * ⚠ หัวข้อต้องอยู่หน้าเดียวกับบรรทัดแรกของเนื้อหาเสมอ
 *   pdfmake ขึ้นหน้าใหม่ตรงไหนก็ได้ ผลคือหัวข้อ "ข้อจำกัดของข้อมูล" ค้างอยู่ท้ายหน้า 1
 *   แล้วเนื้อหาไปเริ่มหน้า 2 ซึ่งในหนังสือราชการอ่านเหมือนข้อนั้นไม่มีเนื้อหา
 *   วิธีแก้คือมัดหัวข้อกับก้อนแรกไว้ด้วยกันด้วย unbreakable ไม่ใช่มัดทั้งข้อ
 *   (มัดทั้งข้อจะดันข้อที่ยาวกว่าหนึ่งหน้าไปทั้งก้อนแล้วเหลือหน้าว่าง)
 */
function section(no: number, title: string, body: Content[]): Content[] {
  const head: Content = { text: `${thNum(no)}. ${title}`, style: 'h2' };
  if (body.length === 0) return [head];
  return [{ unbreakable: true, stack: [head, body[0]!] }, ...body.slice(1)];
}

function para(text: string): Content {
  return { text, style: 'p' };
}

/** เส้นสำหรับเขียนด้วยมือ — ต้องมีจริงในไฟล์ ไม่ใช่ปล่อยว่างให้คนขีดเอง */
function writeLine(width: number, top: number): Content {
  return {
    margin: [0, top, 0, 0],
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: width, y2: 0, lineWidth: 0.6, lineColor: RULE }],
  };
}

/**
 * ๑. ตำแหน่งที่พิจารณา
 *
 * ⚠ ถ้อยคำต่างกันระหว่าง "อัตราเดียว" กับ "หลายอัตรา" โดยตั้งใจ
 *   หนังสือเสนอที่ขึ้นต้นว่า "ตำแหน่งที่พิจารณา" แล้วตามด้วยรายการสิบบรรทัด
 *   อ่านไม่ออกว่าตกลงเสนอกี่เรื่อง
 */
/** ⚠ อัตราบางรายการในข้อมูลจริงไม่มีเลขที่ตำแหน่ง ต้องยังพิมพ์ลงเอกสารได้
    ห้ามตกหล่นเงียบ ๆ เพราะนั่นคือรายการที่ผู้ใช้เสนอไปแล้ว */
const postKey = (it: DraftItem): string => it.target.posNo ?? `${it.target.title} ${it.target.unit}`;
const postNoText = (it: DraftItem): string =>
  it.target.posNo ? `เลขที่ ${it.target.posNo}` : 'ไม่มีเลขที่ตำแหน่งในทะเบียน';

function sectionPosts(items: DraftItem[]): Content[] {
  const posts = new Map<string, DraftItem>();
  for (const it of items) posts.set(postKey(it), it);

  if (posts.size === 1) {
    const it = [...posts.values()][0]!;
    const vacant = it.target.vacantMonths;
    return [
      para(
        `${it.target.title} ${postNoText(it)} สังกัด${it.target.unit}` +
          (vacant !== null ? ` ตำแหน่งดังกล่าวว่างมาแล้ว ${durationTH(vacant)}` : ''),
      ),
    ];
  }

  return [
    para(
      `รอบนี้เสนอย้ายเพื่อแต่งตั้งรวม ${thNum(items.length)} ราย ` +
        `ในตำแหน่งว่าง ${thNum(posts.size)} อัตรา ดังนี้`,
    ),
    {
      style: 'p',
      margin: [14, 4, 0, 0] as [number, number, number, number],
      ul: [...posts.values()].map(
        (it) =>
          `${it.target.title} ${postNoText(it)} สังกัด${leafUnit(it.target.unit)}` +
          (it.target.vacantMonths !== null ? ` (ว่างมาแล้ว ${durationTH(it.target.vacantMonths)})` : ''),
      ),
    },
  ];
}

/** ๒. บัญชีผู้ได้รับการพิจารณา — สี่คอลัมน์ตามแบบร่าง ไม่ใช่สิบสามคอลัมน์ */
function sectionTable(items: DraftItem[]): Content {
  const th = (text: string): Content => ({ text, style: 'th' });

  const head: Content[] = [
    th('ที่'),
    th('ผู้ถูกย้าย · ตำแหน่งและหน่วยงาน'),
    th('ครองตำแหน่ง'),
    th('เหตุผลตามกฎ ก.พ. ข้อ ๓'),
  ];

  const body = items.map((it, i): Content[] => {
    const clauses =
      it.reason && it.reason.clauses.length > 0
        ? it.reason.clauses.map((c) => `(${thNum(Number(c))}) ${clauseLabel(c)}`).join(' · ')
        : '';
    const reasonText = it.reason?.text?.trim() ?? '';
    const pending = isLevelDown(it) && !it.consentAttached ? 'ยังไม่ได้แนบหนังสือยินยอมตามข้อ ๖' : '';

    /* ⚠ ช่องที่ไม่มีข้อมูลต้องพิมพ์ว่าไม่มี ห้ามเว้นว่าง
       ช่องว่างในเอกสารที่เสนอผู้บริหาร อ่านได้ว่าลืมกรอก ไม่ใช่ว่าไม่มี */
    const reasonCell: Content =
      clauses || reasonText
        ? {
            stack: [
              ...(clauses ? [{ text: clauses, bold: true }] : []),
              ...(reasonText ? [{ text: reasonText, margin: [0, 2, 0, 0] }] : []),
              ...(pending ? [{ text: pending, color: MUTED, margin: [0, 2, 0, 0] }] : []),
            ],
          }
        : { text: 'ยังไม่ได้ระบุเหตุผล', color: MUTED };

    return [
      { text: thNum(i + 1), alignment: 'center' },
      {
        stack: [
          { text: it.person.name, bold: true },
          { text: `${it.person.title} ${leafUnit(it.person.unit)}`, color: INK2, margin: [0, 1, 0, 0] },
          {
            text: `ให้ดำรงตำแหน่ง ${it.target.title} ${leafUnit(it.target.unit)} ${postNoText(it)}`,
            color: NAVY,
            margin: [0, 2, 0, 0],
          },
        ],
      },
      { text: durationTH(it.person.monthsInPosition) },
      reasonCell,
    ];
  });

  return {
    margin: [0, 6, 0, 0] as [number, number, number, number],
    table: {
      headerRows: 1,
      widths: [22, '*', 76, 162],
      body: [head, ...body],
    },
    layout: {
      /* ⚠ padding ซ้ายขวาต้องเป็นศูนย์ แล้วเว้นระยะด้วย margin ของเซลล์แทน
         pdfmake คิดความกว้าง '*' โดยไม่หัก padding ออกก่อน ตารางจะล้นขอบกระดาษ
         เท่ากับผลรวม padding พอดี แล้วคอลัมน์ขวาสุดถูกตัดหาย — เคยเกิดมาแล้วสองครั้ง */
      paddingLeft: (i: number) => (i === 0 ? 0 : 7),
      paddingRight: () => 0,
      paddingTop: () => 6,
      paddingBottom: () => 6,
      hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
        i === 0 || i === 1 || i === node.table.body.length ? 0.8 : 0.4,
      vLineWidth: () => 0,
      hLineColor: (i: number, node: { table: { body: unknown[] } }) =>
        i <= 1 || i === node.table.body.length ? INK : LINE,
    },
  };
}

/** ๓. ข้อเท็จจริงที่คำนวณจากทะเบียนตำแหน่ง */
function sectionFacts(all: DraftItem[]): Content[] {
  const s = summarise(all);
  const lines: string[] = [];

  lines.push(
    `ร่างนี้ปิดอัตราว่างเดิม ${thNum(s.closed)} อัตรา และเปิดอัตราว่างใหม่ ${thNum(s.opened)} อัตรา ` +
      (s.net === 0
        ? 'จำนวนอัตราว่างทั้งหมดของสำนักงานจึงเท่าเดิม'
        : s.net > 0
          ? `จำนวนอัตราว่างทั้งหมดของสำนักงานจึงเพิ่มขึ้น ${thNum(s.net)} อัตรา`
          : `จำนวนอัตราว่างทั้งหมดของสำนักงานจึงลดลง ${thNum(Math.abs(s.net))} อัตรา`),
  );

  /* หน่วยที่เสียคนสุทธิตั้งแต่สองคนขึ้นไป เป็นเรื่องที่ผู้บริหารถามเสมอ */
  const losing = s.unitDelta.filter((u) => u.delta <= -2);
  if (losing.length > 0) {
    lines.push(
      'หน่วยงานที่กำลังคนลดลงตั้งแต่สองอัตราขึ้นไปในรอบนี้ ได้แก่ ' +
        losing.map((u) => `${leafUnit(u.unit)} (ลดลง ${thNum(Math.abs(u.delta))} อัตรา)`).join(' · '),
    );
  }

  if (s.openedFrom.length > 0) {
    lines.push(
      `อัตราที่ว่างลงจากการย้ายในรอบนี้และยังไม่มีผู้มาดำรงตำแหน่งแทน มี ${thNum(s.openedFrom.length)} อัตรา ` +
        'ซึ่งต้องพิจารณาในรอบถัดไป',
    );
  }

  lines.push(
    'ตัวเลขในข้อนี้คำนวณจากทะเบียนตำแหน่งโดยตรง มิได้มาจากการประมวลผลด้วยปัญญาประดิษฐ์',
  );

  return lines.map(para);
}

/**
 * ๔. ประวัติการปรับเกณฑ์ — มีเฉพาะเมื่อมีการวิเคราะห์ซ้ำจริง
 *
 * ⚠ ข้อนี้ต้องติดไปกับเอกสารเสมอเมื่อมีข้อมูล ห้ามตัดออกเพื่อความสั้น
 *   การวนปรับเกณฑ์จนคนที่ต้องการโผล่ขึ้นมา คือสิ่งที่ทำให้คำสั่งแต่งตั้งถูกโต้แย้งได้
 *   ถ้าเกณฑ์ที่เปลี่ยนกลางทางไม่ปรากฏในเอกสาร หน้าจอเทียบเกณฑ์จะกลายเป็น
 *   เครื่องมือหาเหตุผลย้อนหลังให้คนที่เลือกไว้แล้ว
 */
function sectionRounds(items: DraftItem[]): { lines: Content[]; has: boolean } {
  const out: Content[] = [];
  const seen = new Set<string>();

  for (const it of items) {
    const posNo = it.target.posNo;
    if (!posNo || seen.has(posNo)) continue;
    seen.add(posNo);
    const b = boardOf(posNo);
    if (b.rounds.length === 0) continue;

    out.push({
      text: `ตำแหน่ง ${it.target.title} เลขที่ ${posNo}`,
      style: 'p',
      bold: true,
      margin: [0, 6, 0, 2] as [number, number, number, number],
    });
    out.push({
      style: 'small',
      margin: [14, 0, 0, 0] as [number, number, number, number],
      ol: b.rounds.map(
        (r) =>
          `${thaiDate(new Date(r.at))} — เกณฑ์ที่ใช้ ` +
          (r.criteria.map((c) => `${c.id} ${c.label}`).join(' · ') || 'ยังไม่ได้ตั้งเกณฑ์'),
      ),
    });
  }

  return { lines: out, has: out.length > 0 };
}

/** ตัวบทกฎหมาย — ใช้เกณฑ์เดียวกับที่หน้าจอใช้แยกกล่องข้อจำกัดออกจากตัวบท */
const isLegalCitation = (w: string): boolean => /^(กฎ ก\.พ\.|มาตรา )/.test(w);

/**
 * ๖. ข้อความกำกับจากระบบ
 *
 * ⚠ คำเตือนจากฝั่งเซิร์ฟเวอร์ต้องติดไปกับไฟล์เสมอ ห้ามตัดทิ้งเพื่อให้เอกสารสั้นลง
 *   (เหตุผลเต็มอยู่หัวไฟล์ exportTable.ts — ไฟล์ที่ดาวน์โหลดแล้วหลุดจากบริบทของหน้าจอ)
 *
 * ⚠ แต่ห้ามเอามากองไว้ในข้อ "ข้อจำกัดของข้อมูล" ด้วย
 *   ไฟล์รุ่นก่อนพิมพ์ทั้งสิบเอ็ดข้อเป็นย่อหน้าต่อกันจนกินไปทั้งหน้า แล้วไม่มีใครอ่าน
 *   ซึ่งเป็นปัญหาเดียวกับที่แก้ไปแล้วบนหน้าจอในรอบสี่ — วิธีแก้ก็ต้องเหมือนกัน
 *   คือแยกข้อที่เปลี่ยนวิธีอ่านเอกสาร ออกจากตัวบทที่ยกมาอ้าง
 */
function sectionNotes(planWarnings: string[]): Content[] {
  const limits = planWarnings.filter((w) => !isLegalCitation(w));
  const laws = planWarnings.filter(isLegalCitation);
  const out: Content[] = [];

  if (limits.length > 0) {
    out.push({
      style: 'small',
      margin: [14, 4, 0, 0] as [number, number, number, number],
      ol: limits,
    });
  }
  if (laws.length > 0) {
    out.push({
      text: 'ตัวบทที่เอกสารฉบับนี้อ้างถึง',
      style: 'small',
      bold: true,
      margin: [0, 10, 0, 0] as [number, number, number, number],
    });
    out.push({
      style: 'small',
      margin: [14, 2, 0, 0] as [number, number, number, number],
      ul: laws,
    });
  }
  return out;
}

/** ข้อจำกัดของข้อมูล — ความเรียงสั้น ๆ ไม่ใช่รายการคำเตือนสิบเอ็ดบรรทัด */
function sectionLimits(all: DraftItem[]): Content[] {
  const items = all.filter((it) => it.status === 'proposed');
  const considered = all.length - items.length;
  const noReason = items.filter((it) => !it.reason || it.reason.clauses.length === 0).length;

  const out: Content[] = [
    para(
      'กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อนข้าราชการพลเรือนสามัญ พ.ศ. ๒๕๖๔ ข้อ ๓ ' +
        'กำหนดให้พิจารณาโดยคำนึงถึงศักยภาพ ผลสัมฤทธิ์ของงาน สมรรถนะ ประวัติการรับราชการ ' +
        'ความรู้ความสามารถ ตลอดจนพฤติกรรมทางจริยธรรมและความประพฤติ ' +
        'ระบบสารสนเทศมีหลักฐานประกอบเพียงข้อประวัติการรับราชการ ' +
        'ประการอื่นต้องใช้เอกสารจากกองการเจ้าหน้าที่ประกอบการพิจารณา',
    ),
    para(
      'การย้ายตามบัญชีนี้เป็นการย้ายในระดับเดิม มิใช่การเลื่อนระดับ ' +
        'จึงมิได้ตรวจมาตรฐานกำหนดตำแหน่ง และอยู่ในดุลพินิจของผู้มีอำนาจสั่งบรรจุ ' +
        'ตามกฎ ก.พ. ฉบับเดียวกัน ข้อ ๘',
    ),
  ];

  if (considered > 0) {
    out.push(
      para(
        `บัญชีผู้ได้รับการพิจารณาของรอบนี้ยังมีผู้ที่อยู่ระหว่างพิจารณาอีก ${thNum(considered)} ราย ` +
          'ซึ่งยังไม่ได้ติดธงเสนอ จึงไม่ปรากฏในข้อ ๒ ของเอกสารฉบับนี้',
      ),
    );
  }
  if (noReason > 0) {
    out.push(
      para(
        `รายการในบัญชีข้อ ๒ ยังไม่ได้ระบุเหตุผล ${thNum(noReason)} ราย ` +
          'ซึ่งต้องระบุก่อนนำเสนอเพื่อลงนาม',
      ),
    );
  }

  out.push({
    text:
      'เอกสารฉบับนี้เป็นการรวบรวมข้อเท็จจริงเพื่อประกอบการพิจารณาเท่านั้น ' +
      'มิใช่การจัดลำดับความเหมาะสม และมิใช่ข้อเสนอว่าควรแต่งตั้งผู้ใด',
    style: 'p',
    bold: true,
  });

  return out;
}

export async function exportRotationMemo(
  all: DraftItem[],
  planWarnings: string[],
): Promise<void> {
  const items = all.filter((it) => it.status === 'proposed');

  const [{ default: pdfMake }, fonts] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('./sarabun.js'),
  ]);

  pdfMake.addVirtualFileSystem({
    'Sarabun-Regular.ttf': fonts.SARABUN_REGULAR,
    'Sarabun-SemiBold.ttf': fonts.SARABUN_SEMIBOLD,
  });
  pdfMake.addFonts({
    Sarabun: {
      normal: 'Sarabun-Regular.ttf',
      bold: 'Sarabun-SemiBold.ttf',
      italics: 'Sarabun-Regular.ttf',
      bolditalics: 'Sarabun-SemiBold.ttf',
    },
  });

  const title = 'บัญชีผู้ได้รับการพิจารณาย้ายเพื่อแต่งตั้ง';
  const now = new Date();
  const rounds = sectionRounds(items);
  let no = 0;

  const content: Content[] = [
    { text: title, style: 'h1', alignment: 'center' },
    {
      text: 'เอกสารแนบท้ายบันทึกเสนอ · สำนักงานปลัดกระทรวงการคลัง',
      style: 'sub',
      alignment: 'center',
    },
    { text: `จัดทำเมื่อ ${thaiDate(now)}`, style: 'byline', alignment: 'center' },
    {
      margin: [0, 14, 0, 16] as [number, number, number, number],
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 493, y2: 0, lineWidth: 1.5, lineColor: NAVY }],
    },

    ...section((no += 1), 'ตำแหน่งที่พิจารณา', sectionPosts(items)),
    ...section((no += 1), 'บัญชีผู้ได้รับการพิจารณา', [sectionTable(items)]),
    ...section((no += 1), 'ข้อเท็จจริงที่คำนวณจากทะเบียนตำแหน่ง', sectionFacts(all)),

    ...(rounds.has
      ? section((no += 1), 'ประวัติการปรับเกณฑ์ที่ใช้พิจารณา', rounds.lines)
      : []),

    ...section((no += 1), 'ข้อจำกัดของข้อมูล', sectionLimits(all)),

    ...(planWarnings.length > 0
      ? section((no += 1), 'ข้อความกำกับจากระบบ', sectionNotes(planWarnings))
      : []),

    /* ⚠ ที่ลงนามต้องอยู่หน้าเดียวกับข้อสุดท้ายเสมอ ห้ามให้ลอยไปหน้าถัดไปลำพัง
       หน้ากระดาษที่มีแต่เส้นให้เซ็น อ่านไม่ออกว่ากำลังเซ็นอนุมัติอะไร */
    {
      unbreakable: true,
      margin: [0, 22, 0, 0] as [number, number, number, number],
      stack: [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 493, y2: 0, lineWidth: 0.6, lineColor: LINE }],
        },
        { text: 'ความเห็นของผู้มีอำนาจสั่งบรรจุ', style: 'h2', margin: [0, 12, 0, 0] },
        writeLine(493, 22),
        writeLine(493, 22),
        {
          margin: [0, 26, 0, 0] as [number, number, number, number],
          columns: [
            { text: '', width: '*' },
            {
              width: 200,
              stack: [
                {
                  canvas: [
                    { type: 'line', x1: 0, y1: 22, x2: 200, y2: 22, lineWidth: 0.6, lineColor: RULE },
                  ],
                },
                { text: '(                                   )', style: 'sign', alignment: 'center', margin: [0, 6, 0, 0] },
                { text: 'ตำแหน่ง', style: 'sign', alignment: 'center' },
              ],
            },
          ],
        },
      ],
    },
  ];

  pdfMake
    .createPdf({
      info: { title, creator: 'YANA' },
      /* ⚠ แนวตั้งเสมอ — ต่างจาก exportTable.ts ที่เป็นแนวนอน
         หนังสือราชการที่แนบเรื่องเสนอเป็นแนวตั้ง และตารางสี่คอลัมน์พอดีหน้าอยู่แล้ว */
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [51, 48, 51, 56],
      defaultStyle: { font: 'Sarabun', fontSize: 12, lineHeight: 1.5, color: INK },
      styles: {
        h1: { fontSize: 16, bold: true, lineHeight: 1.35 },
        sub: { fontSize: 12, color: INK2, margin: [0, 3, 0, 0] },
        byline: { fontSize: 11, color: MUTED, margin: [0, 2, 0, 0] },
        h2: { fontSize: 13, bold: true, margin: [0, 14, 0, 4] },
        p: { fontSize: 12, lineHeight: 1.55, margin: [0, 3, 0, 0] },
        small: { fontSize: 11, lineHeight: 1.55, color: INK2 },
        th: { fontSize: 10.5, bold: true },
        sign: { fontSize: 11, color: INK2, lineHeight: 1.5 },
      },
      footer: (page: number, total: number) => ({
        /* • มีในฟอนต์ที่ฝังไว้ · ⚠ ไม่มี — ดูหมายเหตุหัวไฟล์ */
        text: `${title} · หน้า ${thNum(page)}/${thNum(total)}`,
        alignment: 'center',
        fontSize: 9,
        color: MUTED,
        margin: [0, 14, 0, 0],
      }),
      content,
    })
    .download(fileName('ร่างรอบโยกย้ายและสับเปลี่ยนตำแหน่ง', 'pdf'));
}
